import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, FileText, X, CheckCircle, AlertCircle, Loader2, FileUp, Code, Palette, MessageSquare, ChevronRight, ChevronLeft, BookOpen, HelpCircle, CheckSquare, Calculator, Trash2, RefreshCw, ListChecks, Building2, Award, ArrowLeft } from 'lucide-react';
import { supabase, getSupabaseUrl } from '../../supabase/client';
import { parsePaperFilename, ParsedPaperFilename, buildLevelName } from '../../utils/paperFilenameParser';
import { categoryStore, Organization as StoreOrganization, CourseCategory as StoreCourseCategory, Level } from '../../store/categoryStore';

type CourseCategoryType = 'scratch' | 'python' | 'cpp' | 'other';
type QuestionType = 'single_choice' | 'multi_choice' | 'judge' | 'programming';

interface CourseCategoryConfig {
  type: string;
  name: string;
  programmingName: string;
  icon: React.ElementType;
  color: string;
  bgColor: string;
}

// 根据分类名称推断内部类型：
//   - scratch/python/cpp：对应编程题编辑器
//   - other：所有其他分类（含机器人技术等），非客观题识别为"主观题"
const inferCourseCategoryFromName = (name: string): CourseCategoryType => {
  if (name.includes('图形化')) return 'scratch';
  if (name.includes('Python')) return 'python';
  if (name.includes('C++') || name.includes('C语言')) return 'cpp';
  return 'other';
};

// 后台版：课程分类配置从 categoryStore 动态获取，与【类目与定价】栏目实时同步
const getCourseCategoryConfigs = (): CourseCategoryConfig[] => {
  const categories = categoryStore.getCategories();
  const iconMap: Record<string, React.ElementType> = { 'scratch': Palette, 'python': Code, 'cpp': Code, 'other': MessageSquare };
  const colorMap: Record<string, string> = { 'scratch': 'text-purple-600', 'python': 'text-blue-600', 'cpp': 'text-indigo-600', 'other': 'text-green-600' };
  const bgColorMap: Record<string, string> = { 'scratch': 'bg-purple-500', 'python': 'bg-blue-500', 'cpp': 'bg-indigo-500', 'other': 'bg-green-500' };
  return categories.map(cat => {
    const inferredType = inferCourseCategoryFromName(cat.name);
    const programmingName = inferredType === 'other' ? '主观题' : `${cat.name}题`;
    return {
      type: cat.code || cat.id,
      name: cat.name,
      programmingName,
      icon: iconMap[inferredType] || Code,
      color: colorMap[inferredType] || 'text-gray-600',
      bgColor: bgColorMap[inferredType] || 'bg-gray-500',
    };
  });
};

// 教师版：硬编码4个固定分类（不依赖 categoryStore）
const teacherCategoryConfigs: CourseCategoryConfig[] = [
  { type: 'scratch', name: '图形化编程', programmingName: '图形化编程题', icon: Palette, color: 'text-purple-600', bgColor: 'bg-purple-500' },
  { type: 'python', name: 'Python编程', programmingName: 'Python编程题', icon: Code, color: 'text-blue-600', bgColor: 'bg-blue-500' },
  { type: 'cpp', name: 'C++编程', programmingName: 'C++编程题', icon: Code, color: 'text-indigo-600', bgColor: 'bg-indigo-500' },
  { type: 'other', name: '其它', programmingName: '主观题', icon: MessageSquare, color: 'text-green-600', bgColor: 'bg-green-500' },
];

const defaultCourseCategoryConfig: CourseCategoryConfig = {
  type: 'other',
  name: '其他',
  programmingName: '编程题',
  icon: Code,
  color: 'text-gray-600',
  bgColor: 'bg-gray-500',
};

interface QuestionTypeConfig {
  type: QuestionType;
  name: string;
  icon: React.ElementType;
  isObjective: boolean;
}

const questionTypes: QuestionTypeConfig[] = [
  { type: 'single_choice', name: '单选题', icon: HelpCircle, isObjective: true },
  { type: 'multi_choice', name: '多选题', icon: CheckSquare, isObjective: true },
  { type: 'judge', name: '判断题', icon: Calculator, isObjective: true },
  { type: 'programming', name: '编程题', icon: Code, isObjective: false },
];

interface QuestionConfigItem {
  count: number;
  score: number;
  hasCorrectAnswer: boolean;
  hasExplanation: boolean;
  hasReferenceAnswer: boolean;
  hasScoringPoints: boolean;
}

interface FileItem {
  id: string;
  file: File;
  name: string;
  size: number;
  status: 'pending' | 'reading' | 'uploading' | 'parsing' | 'success' | 'error';
  progress: number;
  message: string;
  questionCount?: number;
  pendingPaperId?: string;
  parsedInfo?: ParsedPaperFilename;
}

const MAX_FILES = 30;
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ALLOWED_EXTENSIONS = ['docx'];

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const generateId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

interface PaperUploadProps {
  isTeacherMode?: boolean;
}

export default function PaperUpload({ isTeacherMode = false }: PaperUploadProps = {}) {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [courseCategory, setCourseCategory] = useState<CourseCategoryType>('scratch');
  const [questionConfig, setQuestionConfig] = useState<Record<QuestionType, QuestionConfigItem>>({
    single_choice: { count: 0, score: 0, hasCorrectAnswer: true, hasExplanation: true, hasReferenceAnswer: false, hasScoringPoints: false },
    multi_choice: { count: 0, score: 0, hasCorrectAnswer: true, hasExplanation: true, hasReferenceAnswer: false, hasScoringPoints: false },
    judge: { count: 0, score: 0, hasCorrectAnswer: true, hasExplanation: true, hasReferenceAnswer: false, hasScoringPoints: false },
    programming: { count: 0, score: 0, hasCorrectAnswer: false, hasExplanation: false, hasReferenceAnswer: true, hasScoringPoints: true },
  });
  const [files, setFiles] = useState<FileItem[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);

  // 后台版专属 state（教师版不使用）
  const [organizations, setOrganizations] = useState<StoreOrganization[]>([]);
  const [selectedOrganization, setSelectedOrganization] = useState<string>('');
  const [categories, setCategories] = useState<StoreCourseCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [levels, setLevels] = useState<Level[]>([]);
  const [selectedLevel, setSelectedLevel] = useState<string>('');
  const [loadingData, setLoadingData] = useState(false);

  // 教师版专属 state：硬编码分类选择
  const [selectedCourseCategory, setSelectedCourseCategory] = useState<string>('scratch');

  // 课程分类配置：教师版用硬编码，后台版动态读取 categoryStore
  const courseCategoryConfigs = useMemo(
    () => isTeacherMode ? teacherCategoryConfigs : getCourseCategoryConfigs(),
    [isTeacherMode, categories]
  );

  // inferredCourseCategory 推导：教师版直接取，后台版从分类名推断
  const selectedCategoryName = selectedCategory ? categories.find(c => c.id === selectedCategory)?.name || '' : '';
  const inferredCourseCategory = isTeacherMode
    ? (selectedCourseCategory as CourseCategoryType)
    : (selectedCategoryName ? inferCourseCategoryFromName(selectedCategoryName) : 'other');
  const inferredConfig = courseCategoryConfigs.find(c => c.type === inferredCourseCategory) || defaultCourseCategoryConfig;

  // 后台版：加载 categoryStore（教师版跳过）
  useEffect(() => {
    if (isTeacherMode) return;
    const loadData = async () => {
      setLoadingData(true);
      try {
        await categoryStore.init();
        setOrganizations(categoryStore.getOrganizations());
        setCategories(categoryStore.getCategories());
        setLevels(categoryStore.getLevels());
      } catch (err) {

      } finally {
        setLoadingData(false);
      }
    };
    loadData();

    const unsubscribe = categoryStore.subscribe(() => {
      setOrganizations(categoryStore.getOrganizations());
      setCategories(categoryStore.getCategories());
      setLevels(categoryStore.getLevels());
    });

    return () => unsubscribe();
  }, [isTeacherMode]);

  const filteredCategories = useMemo(() => {
    if (!selectedOrganization) return [];
    return categories.filter(c => c.orgId === selectedOrganization);
  }, [categories, selectedOrganization]);

  const filteredLevels = useMemo(() => {
    if (!selectedCategory) return [];
    return levels.filter(l => l.catId === selectedCategory);
  }, [levels, selectedCategory]);

  // 后台版：组织变化时清空分类/级别（教师版跳过）
  useEffect(() => {
    if (isTeacherMode) return;
    if (selectedOrganization) {
      const cats = categories.filter(c => c.orgId === selectedOrganization);
      if (cats.length > 0 && !cats.find(c => c.id === selectedCategory)) {
        setSelectedCategory('');
        setSelectedLevel('');
      }
    }
  }, [isTeacherMode, selectedOrganization, categories, selectedCategory]);

  // 后台版：分类变化时清空级别（教师版跳过）
  useEffect(() => {
    if (isTeacherMode) return;
    if (selectedCategory) {
      const levs = levels.filter(l => l.catId === selectedCategory);
      if (levs.length > 0 && !levs.find(l => l.id === selectedLevel)) {
        setSelectedLevel('');
      }
    }
  }, [isTeacherMode, selectedCategory, levels, selectedLevel]);

  // 后台版：选择级别后自动填充题型数量（教师版跳过）
  useEffect(() => {
    if (isTeacherMode) return;
    if (selectedLevel) {
      const level = levels.find(l => l.id === selectedLevel);
      if (level) {
        setQuestionConfig(prev => ({
          ...prev,
          single_choice: { ...prev.single_choice, count: level.singleChoiceCount || 0 },
          multi_choice: { ...prev.multi_choice, count: level.multiChoiceCount || 0 },
          judge: { ...prev.judge, count: level.judgeCount || 0 },
          programming: { ...prev.programming, count: level.programmingCount || 0 },
        }));
      }
    }
  }, [isTeacherMode, selectedLevel, levels]);

  const totalQuestions = useMemo(() => {
    return Object.values(questionConfig).reduce((sum, item) => sum + item.count, 0);
  }, [questionConfig]);

  const totalScore = useMemo(() => {
    return Object.values(questionConfig).reduce((sum, item) => sum + item.count * item.score, 0);
  }, [questionConfig]);

  // 题型摘要：生成 [{name, count, score}] 数组，过滤 count=0 的题型
  // 编程题名称根据所选课程分类动态变化（如 Python → "Python编程题"，其它 → "主观题"）
  const questionSummary = useMemo(() => {
    return questionTypes
      .map(qt => {
        const config = questionConfig[qt.type];
        const name = qt.type === 'programming' ? inferredConfig.programmingName : qt.name;
        return { name, count: config.count, score: config.score };
      })
      .filter(item => item.count > 0);
  }, [questionConfig, inferredConfig]);

  const remainingSlots = MAX_FILES - files.length;

  const stats = useMemo(() => {
    const pending = files.filter(f => f.status === 'pending').length;
    const processing = files.filter(f => ['reading', 'uploading', 'parsing'].includes(f.status)).length;
    const success = files.filter(f => f.status === 'success').length;
    const error = files.filter(f => f.status === 'error').length;
    const done = success + error;
    return { pending, processing, success, error, done, total: files.length };
  }, [files]);

  const allDone = stats.total > 0 && stats.done === stats.total;

  const updateQuestionConfig = (type: QuestionType, field: keyof QuestionConfigItem, value: number | boolean) => {
    setQuestionConfig(prev => ({
      ...prev,
      [type]: { ...prev[type], [field]: value },
    }));
  };

  const handleNext = () => {
    // 后台版：验证必须选择考评体系、课程分类和级别
    if (!isTeacherMode) {
      if (!selectedOrganization) {
        alert('请选择考评体系');
        return;
      }
      if (!selectedCategory) {
        alert('请选择课程分类');
        return;
      }
      if (!selectedLevel) {
        alert('请选择级别分类');
        return;
      }
    }
    // 验证题型配置
    if (totalQuestions === 0) {
      alert('请至少配置一种题型的数量');
      return;
    }
    if (totalScore === 0) {
      alert('请配置题型分值，否则题目分数将为0');
      return;
    }
    if (currentStep < 2) setCurrentStep(prev => prev + 1);
  };

  const handlePrev = () => {
    if (currentStep > 1) setCurrentStep(prev => prev - 1);
  };

  const validateAndAddFiles = (newFiles: File[]) => {
    const validFiles: FileItem[] = [];
    const errors: string[] = [];

    for (const f of newFiles) {
      if (files.length + validFiles.length >= MAX_FILES) {
        errors.push(`最多选择 ${MAX_FILES} 个文件`);
        break;
      }
      const ext = f.name.split('.').pop()?.toLowerCase();
      if (!ALLOWED_EXTENSIONS.includes(ext || '')) {
        errors.push(`${f.name}: 仅支持 .docx 格式`);
        continue;
      }
      if (f.size > MAX_FILE_SIZE) {
        errors.push(`${f.name}: 文件大小超过10MB限制`);
        continue;
      }
      if (files.some(existing => existing.name === f.name && existing.size === f.size)) {
        errors.push(`${f.name}: 文件已添加`);
        continue;
      }
      const parsedInfo = parsePaperFilename(f.name);

      validFiles.push({
        id: generateId(),
        file: f,
        name: f.name,
        size: f.size,
        status: 'pending',
        progress: 0,
        message: '',
        parsedInfo,
      });
    }

    if (errors.length > 0) {
      alert(errors.join('\n'));
    }

    if (validFiles.length > 0) {
      setFiles(prev => [...prev, ...validFiles]);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      validateAndAddFiles(Array.from(e.target.files));
      e.target.value = '';
    }
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      validateAndAddFiles(Array.from(e.dataTransfer.files));
    }
  }, [files]);

  const removeFile = (id: string) => {
    setFiles(prev => prev.filter(f => f.id !== id));
  };

  const clearAllFiles = () => {
    setFiles([]);
  };

  const updateFileItem = (id: string, updates: Partial<FileItem>) => {
    setFiles(prev => prev.map(f => f.id === id ? { ...f, ...updates } : f));
  };

  const processSingleFile = async (id: string) => {
    try {
      updateFileItem(id, { status: 'reading', message: '正在读取文件...', progress: 10 });

      const currentFile = files.find(f => f.id === id);
      if (!currentFile) throw new Error('文件不存在');

      const base64Content = await fileToBase64(currentFile.file);
      updateFileItem(id, { status: 'uploading', message: '正在上传...', progress: 20 });

      const { data: { session } } = await supabase.auth.getSession();

      updateFileItem(id, { status: 'parsing', message: '正在解析试卷内容...', progress: 50 });

      // 后台版：构建级别相关字段
      const selectedOrgName = !isTeacherMode ? (organizations.find(o => o.id === selectedOrganization)?.name || '') : '';
      const selectedLevelName = !isTeacherMode && selectedLevel ? (levels.find(l => l.id === selectedLevel)?.name || '') : '';
      const selectedLevelId = !isTeacherMode ? (selectedLevel || '') : '';
      // 获取选中分类的code（用于后端精确判断题目类型）
      const selectedCategoryCode = !isTeacherMode && selectedCategory ? (categories.find(c => c.id === selectedCategory)?.code || null) : null;

      const response = await fetch(`${getSupabaseUrl()}/functions/v1/paper-processing`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: session ? `Bearer ${session.access_token}` : '',
        },
        body: JSON.stringify({
          fileContent: base64Content,
          fileType: currentFile.file.name.split('.').pop()?.toLowerCase(),
          fileName: currentFile.file.name,
          userId: session?.user?.id,
          courseCategory: inferredCourseCategory,
          categoryCode: selectedCategoryCode,  // 新增：传递原始分类code
          questionConfig: {
            single_choice: { count: questionConfig.single_choice.count, score: questionConfig.single_choice.score },
            multi_choice: { count: questionConfig.multi_choice.count, score: questionConfig.multi_choice.score },
            judge: { count: questionConfig.judge.count, score: questionConfig.judge.score },
            programming: { count: questionConfig.programming.count, score: questionConfig.programming.score },
          },
          extractConfig: {
            extractAnswer: true,
            extractExplanation: true,
            extractScoringPoints: true,
          },
          parsedInfo: isTeacherMode
            ? {
                ...currentFile.parsedInfo,
                categoryType: inferredCourseCategory,
              }
            : {
                ...currentFile.parsedInfo,
                organization: selectedOrgName,
                categoryType: inferredCourseCategory,
                categoryName: selectedCategoryName,
                levelName: selectedLevelName || buildLevelName(inferredCourseCategory, currentFile.parsedInfo?.levelText),
                levelId: selectedLevelId,
              },
          // 教师版新增 dataSource 标识，云函数据此路由到 teacher_pending_papers
          ...(isTeacherMode ? { dataSource: 'teacher' } : {}),
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || '解析失败');
      }

      // 构建成功消息：后台版显示级别警告，教师版不显示
      const warnings: string[] = [];
      if (!isTeacherMode && result.levelMatchWarning) {
        warnings.push(result.levelMatchWarning);
      }
      if (result.abnormalRemark) {
        warnings.push(result.abnormalRemark);
      }
      const successMessage = warnings.length > 0
        ? `成功解析 ${result.questionCount} 道题目（⚠️ ${warnings.join('；')}）`
        : `成功解析 ${result.questionCount} 道题目`;

      updateFileItem(id, {
        status: 'success',
        message: successMessage,
        progress: 100,
        questionCount: result.questionCount,
        pendingPaperId: result.pendingPaperId,
      });
    } catch (error: any) {
      updateFileItem(id, {
        status: 'error',
        message: error.message || '处理失败',
        progress: 0,
      });
    }
  };

  const processAllFiles = async () => {
    const pendingIds = files.filter(f => f.status === 'pending' || f.status === 'error').map(f => f.id);
    if (pendingIds.length === 0) return;

    setIsProcessing(true);

    for (const id of pendingIds) {
      await processSingleFile(id);
    }

    setIsProcessing(false);
  };

  const retryFile = async (id: string) => {
    updateFileItem(id, { status: 'pending', message: '', progress: 0 });
    setIsProcessing(true);
    await processSingleFile(id);
    setIsProcessing(false);
  };

  const resetUpload = () => {
    setFiles([]);
    setIsProcessing(false);
    if (!isTeacherMode) setCurrentStep(1);
    setQuestionConfig({
      single_choice: { count: 0, score: 0, hasCorrectAnswer: true, hasExplanation: true, hasReferenceAnswer: false, hasScoringPoints: false },
      multi_choice: { count: 0, score: 0, hasCorrectAnswer: true, hasExplanation: true, hasReferenceAnswer: false, hasScoringPoints: false },
      judge: { count: 0, score: 0, hasCorrectAnswer: true, hasExplanation: true, hasReferenceAnswer: false, hasScoringPoints: false },
      programming: { count: 0, score: 0, hasCorrectAnswer: false, hasExplanation: false, hasReferenceAnswer: true, hasScoringPoints: true },
    });
  };

  // ============ 文件列表项渲染（教师/后台共用） ============
  const renderFileItem = (fileItem: FileItem) => {
    const isActive = ['reading', 'uploading', 'parsing'].includes(fileItem.status);
    const isDone = fileItem.status === 'success' || fileItem.status === 'error';
    // 教师版紫色，后台版蓝色
    const activeBorderBg = isTeacherMode ? 'border-purple-200 bg-purple-50' : 'border-blue-200 bg-blue-50';
    const activeIconColor = isTeacherMode ? 'text-purple-600' : 'text-blue-600';
    const activeTextColor = isTeacherMode ? 'text-purple-600' : 'text-blue-600';
    const activeBarColor = isTeacherMode ? 'bg-purple-600' : 'bg-blue-600';
    const retryBtnColor = isTeacherMode ? 'text-purple-500 hover:bg-purple-100' : 'text-blue-500 hover:bg-blue-100';

    return (
      <motion.div
        key={fileItem.id}
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, x: -20 }}
        className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
          fileItem.status === 'success' ? 'border-green-200 bg-green-50' :
          fileItem.status === 'error' ? 'border-red-200 bg-red-50' :
          isActive ? activeBorderBg :
          'border-gray-200 bg-white'
        }`}
      >
        <div className="flex-shrink-0">
          {fileItem.status === 'success' ? (
            <CheckCircle className="w-5 h-5 text-green-600" />
          ) : fileItem.status === 'error' ? (
            <AlertCircle className="w-5 h-5 text-red-600" />
          ) : isActive ? (
            <Loader2 className={`w-5 h-5 ${activeIconColor} animate-spin`} />
          ) : (
            <FileText className="w-5 h-5 text-gray-400" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-900 truncate">{fileItem.name}</span>
            <span className="text-xs text-gray-500 flex-shrink-0">{formatFileSize(fileItem.size)}</span>
          </div>
          {fileItem.message && (
            <p className={`text-xs mt-1 ${
              fileItem.status === 'success' ? 'text-green-600' :
              fileItem.status === 'error' ? 'text-red-600' :
              activeTextColor
            }`}>
              {fileItem.message}
            </p>
          )}
          {isActive && (
            <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
              <motion.div
                className={`${activeBarColor} h-1.5 rounded-full`}
                initial={{ width: 0 }}
                animate={{ width: `${fileItem.progress}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
          )}
        </div>

        <div className="flex-shrink-0 flex items-center gap-1">
          {fileItem.status === 'error' && !isProcessing && (
            <button
              onClick={() => retryFile(fileItem.id)}
              className={`p-1.5 rounded-lg transition-colors ${retryBtnColor}`}
              title="重新上传"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          )}
          {!isProcessing && !isDone && (
            <button
              onClick={() => removeFile(fileItem.id)}
              className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              title="移除文件"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </motion.div>
    );
  };

  // ============ 教师版：单页左右分栏布局 ============
  if (isTeacherMode) {
    return (
      <div className="p-6">
        {/* 返回按钮 */}
        <button
          onClick={() => navigate('/teacher/papers')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4"
        >
          <ArrowLeft size={20} />
          <span>返回试卷列表</span>
        </button>

        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">试卷上传</h1>
          <p className="text-blue-600 mt-1">先进行【试卷配置】，再上传文件！仅支持.docx格式，每次最多30份。</p>
        </div>

        {/* 左右分栏布局 */}
        <div className="flex gap-6">
          {/* 左侧：配置区（40%） */}
          <div className="w-[40%] flex-shrink-0">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">试卷配置</h3>
                  <p className="text-xs text-gray-500">选择分类并设置题型</p>
                </div>
              </div>

              {/* 课程分类选择（硬编码4分类） */}
              <div className="mb-5">
                <div className="flex items-center gap-2 mb-2">
                  <BookOpen className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">课程分类</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {teacherCategoryConfigs.map(cat => (
                    <motion.button
                      key={cat.type}
                      onClick={() => {
                        setSelectedCourseCategory(cat.type);
                        setCourseCategory(cat.type as CourseCategoryType);
                      }}
                      className={`px-3 py-1.5 text-sm rounded-lg border-2 transition-all ${
                        selectedCourseCategory === cat.type
                          ? 'border-purple-500 bg-purple-50 text-purple-700 font-medium'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {cat.name}
                    </motion.button>
                  ))}
                </div>
              </div>

              {/* 试卷配置总览 - 汇总信息 + 题型明细 */}
              <div className="mb-5 p-4 bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-xl border border-purple-200/60">
                {/* 第一行：汇总信息（醒目显示） */}
                <div className="flex items-center justify-between mb-3 pb-3 border-b border-purple-200/50">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
                        <ListChecks className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <div className="text-xs text-purple-600 font-medium">总题数</div>
                        <div className="text-xl font-bold text-purple-900 leading-none">{totalQuestions}<span className="text-sm font-normal ml-1">题</span></div>
                      </div>
                    </div>
                    <div className="w-px h-10 bg-purple-200"></div>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
                        <Award className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <div className="text-xs text-purple-600 font-medium">总分值</div>
                        <div className="text-xl font-bold text-purple-900 leading-none">{totalScore}<span className="text-sm font-normal ml-1">分</span></div>
                      </div>
                    </div>
                  </div>
                  {questionSummary.length === 0 && (
                    <div className="text-xs text-purple-500 italic">请配置题型数量</div>
                  )}
                </div>

                {/* 第二行：题型明细（紧凑单行） */}
                {questionSummary.length > 0 && (
                  <div className="text-sm text-gray-700 flex flex-wrap items-center gap-x-4 gap-y-1.5">
                    {questionSummary.map((item, idx) => (
                      <span key={idx} className="inline-flex items-center gap-1">
                        <span className="text-gray-600">{item.name}:</span>
                        <span className="font-semibold text-purple-700">{item.count}</span>
                        <span className="text-gray-500 text-xs">题</span>
                        <span className="text-gray-400">(</span>
                        <span className="font-semibold text-purple-700">{item.score}</span>
                        <span className="text-gray-500 text-xs">分</span>
                        <span className="text-gray-400">)</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* 题型配置表格 */}
              <div className="space-y-2">
                {questionTypes.map((qt) => {
                  const config = questionConfig[qt.type];
                  const isProgramming = qt.type === 'programming';
                  const displayName = isProgramming ? inferredConfig.programmingName : qt.name;
                  const Icon = qt.icon;

                  return (
                    <div key={qt.type} className="border border-gray-200 rounded-xl p-3">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-7 h-7 bg-gray-100 rounded-lg flex items-center justify-center">
                          <Icon className="w-3.5 h-3.5 text-gray-600" />
                        </div>
                        <div className="flex items-center gap-2 flex-1">
                          <span className="font-medium text-gray-900 text-sm">{displayName}</span>
                          <span className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
                            {qt.isObjective ? '客观题' : '非客观题'}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {/* 数量和分值 */}
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <span className="text-xs text-gray-500">数量</span>
                          <input
                            type="number"
                            min={0}
                            value={config.count}
                            onChange={(e) => updateQuestionConfig(qt.type, 'count', parseInt(e.target.value) || 0)}
                            className="w-14 px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                          />
                          <span className="text-xs text-gray-500">题</span>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <span className="text-xs text-gray-500">分值</span>
                          <input
                            type="number"
                            min={0}
                            step={0.5}
                            value={config.score}
                            onChange={(e) => updateQuestionConfig(qt.type, 'score', parseFloat(e.target.value) || 0)}
                            className="w-14 px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                          />
                          <span className="text-xs text-gray-500">分</span>
                        </div>

                        {/* 分隔线 */}
                        <div className="w-px h-6 bg-gray-200 mx-1"></div>

                        {/* 复选框选项 */}
                        <div className="flex flex-wrap items-center gap-3 flex-1">
                          {qt.isObjective ? (
                            <>
                              <label className="flex items-center gap-1 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={config.hasCorrectAnswer}
                                  onChange={(e) => updateQuestionConfig(qt.type, 'hasCorrectAnswer', e.target.checked)}
                                  className="w-3.5 h-3.5 text-purple-600 rounded focus:ring-purple-500"
                                />
                                <span className="text-xs text-gray-700">正确答案</span>
                              </label>
                              <label className="flex items-center gap-1 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={config.hasExplanation}
                                  onChange={(e) => updateQuestionConfig(qt.type, 'hasExplanation', e.target.checked)}
                                  className="w-3.5 h-3.5 text-purple-600 rounded focus:ring-purple-500"
                                />
                                <span className="text-xs text-gray-700">答案解析</span>
                              </label>
                            </>
                          ) : (
                            <>
                              <label className="flex items-center gap-1 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={config.hasReferenceAnswer}
                                  onChange={(e) => updateQuestionConfig(qt.type, 'hasReferenceAnswer', e.target.checked)}
                                  className="w-3.5 h-3.5 text-purple-600 rounded focus:ring-purple-500"
                                />
                                <span className="text-xs text-gray-700">参考答案</span>
                              </label>
                              <label className="flex items-center gap-1 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={config.hasScoringPoints}
                                  onChange={(e) => updateQuestionConfig(qt.type, 'hasScoringPoints', e.target.checked)}
                                  className="w-3.5 h-3.5 text-purple-600 rounded focus:ring-purple-500"
                                />
                                <span className="text-xs text-gray-700">评分标准</span>
                              </label>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* 右侧：上传区（60%） */}
          <div className="w-[60%] flex-shrink-0">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Upload className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">文件上传</h3>
                  <p className="text-xs text-gray-500">仅支持 .docx 格式，每次最多 {MAX_FILES} 份</p>
                </div>
              </div>

              {/* 文件统计和操作按钮 */}
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">
                    已选择 <span className="text-purple-600 font-bold">{files.length}</span>/{MAX_FILES} 个文件
                  </span>
                  {remainingSlots > 0 && (
                    <span className="text-xs text-gray-500">还可添加 {remainingSlots} 个</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {files.length > 0 && !isProcessing && (
                    <button
                      onClick={clearAllFiles}
                      className="text-xs text-red-500 hover:text-red-600 flex items-center gap-1 transition-colors px-2 py-1 rounded-lg hover:bg-red-50"
                    >
                      <Trash2 className="w-3 h-3" />
                      清空全部
                    </button>
                  )}
                </div>
              </div>

              {/* 处理状态提示 */}
              {files.length > 0 && (
                <div className="mb-4">
                  {stats.total > 0 && stats.processing > 0 && (
                    <div className="mb-3 p-3 bg-purple-50 rounded-xl flex items-center gap-3">
                      <Loader2 className="w-4 h-4 text-purple-600 animate-spin" />
                      <span className="text-sm text-purple-700">
                        正在处理 {stats.processing} 份试卷...
                      </span>
                    </div>
                  )}
                  {allDone && (
                    <div className="mb-3">
                      {stats.error === 0 ? (
                        <div className="bg-green-50 rounded-xl p-3 flex items-center gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <span className="text-sm text-green-700">
                            全部完成！成功 {stats.success} 份试卷
                          </span>
                        </div>
                      ) : (
                        <div className="bg-amber-50 rounded-xl p-3 flex items-center gap-3">
                          <AlertCircle className="w-5 h-5 text-amber-600" />
                          <span className="text-sm text-amber-700">
                            处理完成：成功 {stats.success} 份，失败 {stats.error} 份
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* 文件列表 */}
              {files.length > 0 && (
                <div className="mb-4 max-h-[280px] overflow-y-auto space-y-2">
                  <AnimatePresence>
                    {files.map(renderFileItem)}
                  </AnimatePresence>
                </div>
              )}

              {/* 拖拽上传区域 */}
              <div
                className={`border-2 border-dashed rounded-2xl p-6 text-center transition-all ${
                  isProcessing ? 'border-gray-200 bg-gray-50 opacity-60' :
                  remainingSlots <= 0 ? 'border-gray-200 bg-gray-50' :
                  'border-gray-300 hover:border-purple-400 cursor-pointer bg-gray-50 hover:bg-purple-50'
                }`}
                onDragOver={(e) => { if (!isProcessing) e.preventDefault(); }}
                onDrop={isProcessing ? undefined : handleDrop}
              >
                {remainingSlots <= 0 && !isProcessing ? (
                  <div className="text-gray-400">
                    <FileText className="w-8 h-8 mx-auto mb-2" />
                    <p className="text-sm">已达到最大文件数量 ({MAX_FILES})</p>
                  </div>
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-600 text-sm mb-1">
                      {isProcessing ? '上传中，请等待...' : '拖拽文件到此处，或点击选择'}
                    </p>
                    <p className="text-xs text-gray-400 mb-3">支持多选，每个文件不超过 10MB</p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".docx"
                      multiple
                      onChange={handleFileSelect}
                      className="hidden"
                      id="file-input-teacher"
                      disabled={isProcessing || remainingSlots <= 0}
                    />
                    {!isProcessing && remainingSlots > 0 && (
                      <label
                        htmlFor="file-input-teacher"
                        className="inline-flex items-center gap-2 px-5 py-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors cursor-pointer text-sm"
                      >
                        <FileUp className="w-4 h-4" />
                        选择文件
                      </label>
                    )}
                  </>
                )}
              </div>

              {/* 底部操作按钮 */}
              <div className="mt-5 flex items-center justify-between">
                {allDone && stats.success > 0 ? (
                  <>
                    <button
                      onClick={resetUpload}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors text-sm"
                    >
                      继续上传
                    </button>
                    <button
                      onClick={() => navigate('/teacher/papers')}
                      className="px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors flex items-center gap-2 text-sm"
                    >
                      <ListChecks className="w-4 h-4" />
                      查看我的试卷
                    </button>
                  </>
                ) : (
                  <>
                    <div className="text-xs text-red-600 font-medium">
                      {totalQuestions === 0 && '请务必保证题型数量正确！否则会导致解析失败！'}
                      {totalQuestions > 0 && totalScore === 0 && '⚠️ 请配置题型分值，否则题目分数将为0！'}
                      {totalQuestions > 0 && totalScore > 0 && `已配置 ${totalQuestions} 道题，共 ${totalScore} 分`}
                    </div>
                    <button
                      onClick={processAllFiles}
                      disabled={files.length === 0 || isProcessing || totalQuestions === 0 || totalScore === 0 || files.every(f => f.status === 'success')}
                      className="px-5 py-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      {isProcessing ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          处理中...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4" />
                          开始解析 ({files.filter(f => f.status === 'pending' || f.status === 'error').length} 份)
                        </>
                      )}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============ 后台版：两步向导布局 ============
  const renderStep1 = () => (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-4xl mx-auto">
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
            <BookOpen className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">步骤 1：配置试卷</h3>
            <p className="text-sm text-gray-500">选择考评体系、课程分类、级别，并设置题型数量</p>
          </div>
        </div>

        {/* 三级级联选择 */}
        <div className="mb-6 space-y-5">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">考评体系</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {loadingData ? (
                <div className="px-4 py-2 text-sm text-gray-400">加载中...</div>
              ) : organizations.length === 0 ? (
                <div className="px-4 py-2 text-sm text-gray-400">暂无考评体系</div>
              ) : (
                organizations.map(org => (
                  <motion.button
                    key={org.id}
                    onClick={() => { setSelectedOrganization(org.id); setSelectedCategory(''); setSelectedLevel(''); }}
                    className={`px-4 py-2 text-sm rounded-lg border-2 transition-all ${
                      selectedOrganization === org.id
                        ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {org.name}
                  </motion.button>
                ))
              )}
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-2">
              <BookOpen className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">课程分类</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {!selectedOrganization ? (
                <div className="px-4 py-2 text-sm text-gray-400">请先选择考评体系</div>
              ) : filteredCategories.length === 0 ? (
                <div className="px-4 py-2 text-sm text-gray-400">暂无课程分类</div>
              ) : (
                filteredCategories.map(cat => (
                  <motion.button
                    key={cat.id}
                    onClick={() => { setSelectedCategory(cat.id); setSelectedLevel(''); }}
                    className={`px-4 py-2 text-sm rounded-lg border-2 transition-all ${
                      selectedCategory === cat.id
                        ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {cat.name}
                  </motion.button>
                ))
              )}
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Award className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">级别分类</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {!selectedCategory ? (
                <div className="px-4 py-2 text-sm text-gray-400">请先选择课程分类</div>
              ) : filteredLevels.length === 0 ? (
                <div className="px-4 py-2 text-sm text-gray-400">暂无级别</div>
              ) : (
                filteredLevels.map(level => (
                  <motion.button
                    key={level.id}
                    onClick={() => setSelectedLevel(level.id)}
                    className={`px-4 py-2 text-sm rounded-lg border-2 transition-all ${
                      selectedLevel === level.id
                        ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {level.name}
                  </motion.button>
                ))
              )}
            </div>
          </div>
        </div>

        {/* 试卷配置总览 - 汇总信息 + 题型明细 */}
        <div className="mb-6 p-4 bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-xl border border-blue-200/60">
          {/* 第一行：汇总信息（醒目显示） */}
          <div className="flex items-center justify-between mb-3 pb-3 border-b border-blue-200/50">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <ListChecks className="w-4 h-4 text-white" />
                </div>
                <div>
                  <div className="text-xs text-blue-600 font-medium">总题数</div>
                  <div className="text-xl font-bold text-blue-900 leading-none">{totalQuestions}<span className="text-sm font-normal ml-1">题</span></div>
                </div>
              </div>
              <div className="w-px h-10 bg-blue-200"></div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Award className="w-4 h-4 text-white" />
                </div>
                <div>
                  <div className="text-xs text-blue-600 font-medium">总分值</div>
                  <div className="text-xl font-bold text-blue-900 leading-none">{totalScore}<span className="text-sm font-normal ml-1">分</span></div>
                </div>
              </div>
            </div>
            {questionSummary.length === 0 && (
              <div className="text-xs text-blue-500 italic">请配置题型数量</div>
            )}
          </div>

          {/* 第二行：题型明细（紧凑单行） */}
          {questionSummary.length > 0 && (
            <div className="text-sm text-gray-700 flex flex-wrap items-center gap-x-4 gap-y-1.5">
              {questionSummary.map((item, idx) => (
                <span key={idx} className="inline-flex items-center gap-1">
                  <span className="text-gray-600">{item.name}:</span>
                  <span className="font-semibold text-blue-700">{item.count}</span>
                  <span className="text-gray-500 text-xs">题</span>
                  <span className="text-gray-400">(</span>
                  <span className="font-semibold text-blue-700">{item.score}</span>
                  <span className="text-gray-500 text-xs">分</span>
                  <span className="text-gray-400">)</span>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-3">
          {questionTypes.map((qt) => {
            const config = questionConfig[qt.type];
            const isProgramming = qt.type === 'programming';
            const displayName = isProgramming ? inferredConfig.programmingName : qt.name;
            const Icon = qt.icon;

            return (
              <div key={qt.type} className="border border-gray-200 rounded-xl p-4">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-3 flex-shrink-0" style={{ width: '160px' }}>
                    <div className="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center">
                      <Icon className="w-4 h-4 text-gray-600" />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900 text-sm whitespace-nowrap">{displayName}</span>
                      <span className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full whitespace-nowrap">
                        {qt.isObjective ? '客观题' : '非客观题'}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0" style={{ width: '180px' }}>
                    <input
                      type="number"
                      min={0}
                      value={config.count}
                      onChange={(e) => updateQuestionConfig(qt.type, 'count', parseInt(e.target.value) || 0)}
                      className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="数量"
                    />
                    <span className="text-xs text-gray-500 whitespace-nowrap">题</span>
                    <input
                      type="number"
                      min={0}
                      step={0.5}
                      value={config.score}
                      onChange={(e) => updateQuestionConfig(qt.type, 'score', parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="分值"
                    />
                    <span className="text-xs text-gray-500 whitespace-nowrap">分</span>
                  </div>

                  <div className="flex items-center gap-4 flex-1">
                    {qt.isObjective ? (
                      <>
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={config.hasCorrectAnswer}
                            onChange={(e) => updateQuestionConfig(qt.type, 'hasCorrectAnswer', e.target.checked)}
                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">正确答案</span>
                        </label>
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={config.hasExplanation}
                            onChange={(e) => updateQuestionConfig(qt.type, 'hasExplanation', e.target.checked)}
                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">答案解析</span>
                        </label>
                      </>
                    ) : (
                      <>
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={config.hasReferenceAnswer}
                            onChange={(e) => updateQuestionConfig(qt.type, 'hasReferenceAnswer', e.target.checked)}
                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">参考答案</span>
                        </label>
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={config.hasScoringPoints}
                            onChange={(e) => updateQuestionConfig(qt.type, 'hasScoringPoints', e.target.checked)}
                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">评分标准</span>
                        </label>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-8 flex justify-end">
          <button
            onClick={handleNext}
            disabled={totalQuestions === 0}
            className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            下一步
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </motion.div>
  );

  const renderStep2 = () => (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl mx-auto">
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
            <Upload className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">步骤 2：上传试卷文件</h3>
            <p className="text-sm text-gray-500">仅支持 .docx 格式，最多同时上传 {MAX_FILES} 份试卷</p>
          </div>
        </div>

        {/* 试卷配置总览 - 汇总信息 + 题型明细 */}
        <div className="mb-6 p-4 bg-gradient-to-br from-gray-50 to-gray-100/50 rounded-xl border border-gray-200/60">
          {/* 第一行：汇总信息（醒目显示） */}
          <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-200/50">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <ListChecks className="w-4 h-4 text-white" />
                </div>
                <div>
                  <div className="text-xs text-blue-600 font-medium">总题数</div>
                  <div className="text-xl font-bold text-blue-900 leading-none">{totalQuestions}<span className="text-sm font-normal ml-1">题</span></div>
                </div>
              </div>
              <div className="w-px h-10 bg-blue-200"></div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Award className="w-4 h-4 text-white" />
                </div>
                <div>
                  <div className="text-xs text-blue-600 font-medium">总分值</div>
                  <div className="text-xl font-bold text-blue-900 leading-none">{totalScore}<span className="text-sm font-normal ml-1">分</span></div>
                </div>
              </div>
            </div>
            {questionSummary.length === 0 && (
              <div className="text-xs text-gray-500 italic">请配置题型数量</div>
            )}
            {questionSummary.length > 0 && totalScore === 0 && (
              <div className="text-xs text-red-600 font-medium">⚠️ 请配置题型分值，否则题目分数将为0！</div>
            )}
          </div>

          {/* 第二行：题型明细（紧凑单行） */}
          {questionSummary.length > 0 && (
            <div className="text-sm text-blue-900 flex flex-wrap items-center gap-x-4 gap-y-1.5">
              {questionSummary.map((item, idx) => (
                <span key={idx} className="inline-flex items-center gap-1">
                  <span className="text-blue-700">{item.name}:</span>
                  <span className="font-semibold text-blue-900">{item.count}</span>
                  <span className="text-blue-600 text-xs">题</span>
                  <span className="text-blue-400">(</span>
                  <span className="font-semibold text-blue-900">{item.score}</span>
                  <span className="text-blue-600 text-xs">分</span>
                  <span className="text-blue-400">)</span>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">
              已选择 <span className="text-blue-600 font-bold">{files.length}</span>/{MAX_FILES} 个文件
            </span>
            {remainingSlots > 0 && (
              <span className="text-xs text-gray-500">还可添加 {remainingSlots} 个</span>
            )}
          </div>
          {files.length > 0 && !isProcessing && (
            <button
              onClick={clearAllFiles}
              className="text-xs text-red-500 hover:text-red-600 flex items-center gap-1 transition-colors"
            >
              <Trash2 className="w-3 h-3" />
              清空全部
            </button>
          )}
        </div>

        {files.length > 0 && (
          <div className="mb-4">
            {stats.total > 0 && stats.processing > 0 && (
              <div className="mb-3 p-3 bg-blue-50 rounded-xl flex items-center gap-3">
                <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                <span className="text-sm text-blue-700">
                  正在处理 {stats.processing} 份试卷...
                </span>
              </div>
            )}
            {allDone && (
              <div className="mb-3 p-3 rounded-xl flex items-center gap-3">
                {stats.error === 0 ? (
                  <div className="bg-green-50 rounded-xl p-3 flex items-center gap-3 w-full">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-sm text-green-700">
                      全部完成！成功 {stats.success} 份试卷
                    </span>
                  </div>
                ) : (
                  <div className="bg-amber-50 rounded-xl p-3 flex items-center gap-3 w-full">
                    <AlertCircle className="w-5 h-5 text-amber-600" />
                    <span className="text-sm text-amber-700">
                      处理完成：成功 {stats.success} 份，失败 {stats.error} 份
                    </span>
                  </div>
                )}
              </div>
            )}
            <AnimatePresence>
              {files.map(renderFileItem)}
            </AnimatePresence>
          </div>
        )}

        <div
          className={`border-2 border-dashed rounded-2xl p-8 text-center transition-all ${
            isProcessing ? 'border-gray-200 bg-gray-50 opacity-60' :
            remainingSlots <= 0 ? 'border-gray-200 bg-gray-50' :
            'border-gray-300 hover:border-blue-400 cursor-pointer'
          }`}
          onDragOver={(e) => { if (!isProcessing) e.preventDefault(); }}
          onDrop={isProcessing ? undefined : handleDrop}
        >
          {remainingSlots <= 0 && !isProcessing ? (
            <div className="text-gray-400">
              <FileText className="w-10 h-10 mx-auto mb-3" />
              <p className="text-sm">已达到最大文件数量 ({MAX_FILES})</p>
            </div>
          ) : (
            <>
              <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 mb-2">
                {isProcessing ? '上传中，请等待...' : '拖拽文件到此处，或点击选择'}
              </p>
              <p className="text-xs text-gray-400 mb-4">支持多选，每个文件不超过 10MB</p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".docx"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                id="file-input-admin"
                disabled={isProcessing || remainingSlots <= 0}
              />
              {!isProcessing && remainingSlots > 0 && (
                <label
                  htmlFor="file-input-admin"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors cursor-pointer"
                >
                  <FileUp className="w-5 h-5" />
                  选择文件
                </label>
              )}
            </>
          )}
        </div>

        {allDone && stats.success > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6 flex justify-center"
          >
            <button
              onClick={() => navigate('/admin/pending-papers')}
              className="px-6 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              <ListChecks className="w-5 h-5" />
              查看草稿列表
            </button>
          </motion.div>
        )}

        <div className="mt-6 flex justify-between">
          <button
            onClick={handlePrev}
            disabled={isProcessing}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-5 h-5" />
            上一步
          </button>
          {!allDone && (
            <button
              onClick={processAllFiles}
              disabled={files.length === 0 || isProcessing || totalQuestions === 0 || totalScore === 0 || files.every(f => f.status === 'success')}
              className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  处理中...
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5" />
                  开始解析 ({files.filter(f => f.status === 'pending' || f.status === 'error').length} 份)
                </>
              )}
            </button>
          )}
          {allDone && (
            <button
              onClick={resetUpload}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors flex items-center gap-2"
            >
              继续上传
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">试卷上传</h1>
        <p className="text-gray-500 mt-1">两步完成试卷上传：配置试卷 → 上传文件（最多 {MAX_FILES} 份）</p>
      </div>

      <div className="mb-8">
        <div className="flex items-center justify-center gap-4">
          {[1, 2].map((step) => (
            <div key={step} className="flex items-center gap-4">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                  currentStep === step
                    ? 'bg-blue-600 text-white'
                    : currentStep > step
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-200 text-gray-500'
                }`}
              >
                {currentStep > step ? <CheckCircle className="w-5 h-5" /> : step}
              </div>
              {step < 2 && (
                <div
                  className={`w-16 h-1 rounded ${
                    currentStep > step ? 'bg-green-500' : 'bg-gray-200'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
        <div className="flex items-center justify-center gap-24 mt-2 text-sm text-gray-500">
          <span>配置试卷</span>
          <span>上传文件</span>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
      </AnimatePresence>
    </div>
  );
}
